import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    CheckCircle,
    AlertCircle,
    Search,
    Edit2,
    Trash2,
    ChevronDown,
    ChevronRight,
    Plus,
    ChevronsUpDown,
    Loader2,
    RefreshCw
} from 'lucide-react';
import InnerHeader from '../components/common/InnerHeader/InnerHeader';
import InnerFooter from '../components/common/InnerFooter/InnerFooter';
import PoleDesignSidebar from '../components/common/PoleDesignSidebar/PoleDesignSidebar';
import ConfirmDeleteModal from '../components/common/ConfirmDeleteModal/ConfirmDeleteModal';
import './Dashboard.css';
import './DesignComponents.css';
import './styles/pages-common.css';
import { usePageLoader } from '../components/common/PageLoader/PageLoader';

const ROWS_PER_PAGE = 10;

const EMPTY_FORM = {
    code: '',
    name: '',
    category_id: '',
    lookup_uom_id: '',
    unitOfMeasure: '',
    description: '',
    specification: '',
    status: true,
};


// Helper to extract clear, human-readable error messages from API responses
const extractApiErrorMessage = (errData, fallback = 'Operation failed. Please try again.') => {
    if (!errData) return fallback;
    if (typeof errData === 'string') {
        const str = errData.trim();
        if (str.toLowerCase().includes('unique') || str.toLowerCase().includes('already exists') || str.toLowerCase().includes('duplicate')) {
            return 'A component with this code or name already exists.';
        }
        return str || fallback;
    }

    // Check direct string fields
    if (typeof errData.message === 'string' && errData.message.trim()) {
        const msg = errData.message.trim();
        if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('duplicate')) {
            return 'A component with this code or name already exists.';
        }
        return msg;
    }
    if (typeof errData.detail === 'string' && errData.detail.trim()) {
        const msg = errData.detail.trim();
        if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('duplicate')) {
            return 'A component with this code or name already exists.';
        }
        return msg;
    }
    if (typeof errData.error === 'string' && errData.error.trim()) {
        const msg = errData.error.trim();
        if (msg.toLowerCase().includes('unique') || msg.toLowerCase().includes('already exists') || msg.toLowerCase().includes('duplicate')) {
            return 'A component with this code or name already exists.';
        }
        return msg;
    }

    // non_field_errors array (Django REST Framework duplicate / unique_together errors)
    if (Array.isArray(errData.non_field_errors) && errData.non_field_errors.length > 0) {
        const joined = errData.non_field_errors.join(' ');
        if (joined.toLowerCase().includes('unique') || joined.toLowerCase().includes('already exists') || joined.toLowerCase().includes('duplicate')) {
            return 'A component with this code or name already exists.';
        }
        return joined;
    }

    // detail array (FastAPI / Pydantic)
    if (Array.isArray(errData.detail) && errData.detail.length > 0) {
        const details = errData.detail.map((d) => {
            if (typeof d === 'string') return d;
            if (d && typeof d === 'object') {
                const loc = Array.isArray(d.loc) ? d.loc.slice(1).join('.') : '';
                return loc ? `${loc}: ${d.msg || d.message}` : (d.msg || d.message || JSON.stringify(d));
            }
            return String(d);
        });
        const fullDetail = details.join(' | ');
        if (fullDetail.toLowerCase().includes('unique') || fullDetail.toLowerCase().includes('already exists') || fullDetail.toLowerCase().includes('duplicate')) {
            return 'A component with this code or name already exists.';
        }
        return fullDetail;
    }

    // Field-level error dictionaries
    const targetObj = (errData.errors && typeof errData.errors === 'object') ? errData.errors : errData;
    if (typeof targetObj === 'object' && targetObj !== null) {
        const fieldErrors = [];
        for (const [key, val] of Object.entries(targetObj)) {
            if (['status', 'statusCode', 'code', 'success'].includes(key)) continue;
            let valStr = '';
            if (Array.isArray(val)) {
                valStr = val.join(', ');
            } else if (typeof val === 'string') {
                valStr = val;
            } else if (typeof val === 'object' && val !== null) {
                valStr = val.message || val.detail || val.msg || JSON.stringify(val);
            }
            if (valStr) {
                if (valStr.toLowerCase().includes('unique') || valStr.toLowerCase().includes('already exists') || valStr.toLowerCase().includes('duplicate')) {
                    return 'A component with this code or name already exists.';
                }
                const readableKey = key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
                fieldErrors.push(`${readableKey}: ${valStr}`);
            }
        }
        if (fieldErrors.length > 0) {
            return fieldErrors.join(' | ');
        }
    }

    return fallback;
};

export const DesignComponents = ({ setView }) => {
    const [user, setUser] = useState(() => {
        try {
            const stored = localStorage.getItem('user');
            return stored ? JSON.parse(stored) : null;
        } catch {
            return null;
        }
    });

    const [alert, setAlert] = useState(null);
    const [components, setComponents] = useState([]);
    const [categories, setCategories] = useState([]);
    const [uoms, setUoms] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { setPageLoading } = usePageLoader();

    useEffect(() => {
        setPageLoading(isLoading);
        return () => setPageLoading(false);
    }, [isLoading, setPageLoading]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [sortField, setSortField] = useState('id');
    const [sortDir, setSortDir] = useState('asc');
    const [currentPage, setCurrentPage] = useState(1);
    const [expandedRows, setExpandedRows] = useState({});
    const [activeTopNav, setActiveTopNav] = useState('Pole Design');

    // ── Form view state ───────────────────────────────────────────────────────
    const [showForm, setShowForm] = useState(false);
    const [editingComponent, setEditingComponent] = useState(null);
    const [formData, setFormData] = useState(EMPTY_FORM);
    const [formErrors, setFormErrors] = useState({});
    const [pendingDelete, setPendingDelete] = useState(null);

    // ── Fetch user ────────────────────────────────────────────────────────────
    const fetchUserDetails = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return null;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/user/AlluserDetails/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) throw new Error('Failed');
            const data = await res.json();
            if (data.status && data.data?.user) {
                const u = data.data.user;
                if (u.profile_picture && !u.picture) u.picture = u.profile_picture;
                setUser(u);
                localStorage.setItem('user', JSON.stringify(u));
                return u;
            }
            throw new Error('Invalid');
        } catch {
            const stored = localStorage.getItem('user');
            if (stored) {
                const u = JSON.parse(stored);
                setUser(u);
                return u;
            }
            return null;
        }
    };

    // ── Fetch Categories from API ─────────────────────────────────────────────
    const fetchCategories = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/category/`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) return;
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) {
                list = data;
            } else if (data && data.status && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.results)) {
                list = data.results;
            }

            if (list.length > 0) {
                const mapped = list.map(c => ({
                    id: c.category_id ?? c.id ?? c.pk,
                    name: c.category_name || c.name || '',
                }));
                setCategories(mapped);
            }
        } catch (err) {
            console.error('[DesignComponents] Failed to fetch categories:', err);
        }
    };

    // ── Fetch Units of Measure (GET /api/pole-design-uom-lookup) ──────────────
    const fetchUoms = async () => {
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) return;
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/pole-design-uom-lookup`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
            });
            if (!res.ok) return;
            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) {
                list = data;
            } else if (data && data.status && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.data)) {
                list = data.data;
            }

            if (list.length > 0) {
                const mapped = list.map(u => ({
                    id: u.lookup_uom_id ?? u.id,
                    code: u.lookup_uom_code ?? u.code ?? '',
                    value: u.lookup_uom_value ?? u.value ?? '',
                }));
                setUoms(mapped);
            }
        } catch (err) {
            console.error('[DesignComponents] Failed to fetch UOMs:', err);
        }
    };


    // ── Fetch Components List from API (GET /api/components/) ─────────────────
    const fetchComponents = async () => {
        setIsLoading(true);
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (!accessToken) {
                setIsLoading(false);
                return;
            }
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/components/`, {
                method: 'GET',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json'
                },
            });

            if (!res.ok) {
                if (res.status === 401) {
                    localStorage.removeItem('accessToken');
                    if (setView) setView('signin');
                    return;
                }
                throw new Error(`Failed to fetch components (${res.status})`);
            }

            const data = await res.json();
            let list = [];
            if (Array.isArray(data)) {
                list = data;
            } else if (data && data.status && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.data)) {
                list = data.data;
            } else if (data && Array.isArray(data.results)) {
                list = data.results;
            }

            const mapped = list.map(item => ({
                id: item.component_id ?? item.id,
                code: item.component_code ?? item.code ?? '',
                name: item.component_name ?? item.name ?? '',
                category_id: item.category_id,
                category: item.category_name ?? item.category ?? '',
                lookup_uom_id: item.lookup_uom_id,
                unitOfMeasure: item.unit_of_measure ?? item.lookup_uom_value ?? item.unitOfMeasure ?? '',
                description: item.component_description ?? item.description ?? '',
                specification: item.specification ?? '',
                status: item.status !== false,
                created_utc: item.created_utc,
                created_by: item.created_by,
                last_update_date: item.last_update_date,
                last_updated_by: item.last_updated_by,
            }));

            setComponents(mapped);
        } catch (error) {
            console.error('[DesignComponents] Error fetching components:', error);
            setAlert({ type: 'error', message: error.message || 'Failed to load components.' });
            setTimeout(() => setAlert(null), 4000);
            setComponents([]);
        } finally {
            setIsLoading(false);
        }
    };

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        let isMounted = true;
        fetchUserDetails().then(userData => {
            if (!userData && isMounted && setView) {
                setView('signin');
            }
        });
        fetchCategories();
        fetchUoms();
        fetchComponents();

        return () => {
            isMounted = false;
        };
    }, []);


    // ── Search & Sorting Logic ────────────────────────────────────────────────
    const filtered = (components || []).filter(item => {
        if (!searchQuery) return true;
        const q = searchQuery.toLowerCase();
        const catName = (item.category || categories.find(c => String(c.id) === String(item.category_id))?.name || '').toLowerCase();
        return (
            (item.code || '').toLowerCase().includes(q) ||
            (item.name || '').toLowerCase().includes(q) ||
            catName.includes(q) ||
            (item.unitOfMeasure || '').toLowerCase().includes(q) ||
            (item.description || '').toLowerCase().includes(q) ||
            (item.specification || '').toLowerCase().includes(q)
        );
    });

    const sorted = [...filtered].sort((a, b) => {
        let av = a[sortField];
        let bv = b[sortField];
        if (sortField === 'category') {
            av = a.category || categories.find(c => String(c.id) === String(a.category_id))?.name || '';
            bv = b.category || categories.find(c => String(c.id) === String(b.category_id))?.name || '';
        }
        if (typeof av === 'string') av = av.toLowerCase();
        if (typeof bv === 'string') bv = bv.toLowerCase();
        if (av < bv) return sortDir === 'asc' ? -1 : 1;
        if (av > bv) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const totalPages = Math.max(1, Math.ceil(sorted.length / ROWS_PER_PAGE));
    const paginated = sorted.slice((currentPage - 1) * ROWS_PER_PAGE, currentPage * ROWS_PER_PAGE);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDir(d => d === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const toggleRowExpand = (id) => {
        setExpandedRows(prev => ({
            ...prev,
            [id]: !prev[id],
        }));
    };

    // ── Toggle status (PATCH /api/components/{id}/status/) ───────────────────
    const handleToggleStatus = async (id, e) => {
        if (e) e.stopPropagation();
        const targetComp = components.find(c => c.id === id);
        if (!targetComp) return;

        const nextStatus = !targetComp.status;
        const accessToken = localStorage.getItem('accessToken');

        // Optimistic UI update
        setComponents(prev =>
            prev.map(c => (c.id === id ? { ...c, status: nextStatus } : c))
        );

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/components/${id}/status/`, {
                method: 'PATCH',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status: nextStatus }),
            });

            if (!res.ok) {
                throw new Error(`Failed to update status (${res.status})`);
            }

            setAlert({
                type: 'success',
                message: `Component "${targetComp.code}" set to ${nextStatus ? 'Active' : 'Inactive'}.`,
            });
            setTimeout(() => setAlert(null), 3000);
        } catch (error) {
            console.error('[DesignComponents] Status update error:', error);
            // Revert state on failure
            setComponents(prev =>
                prev.map(c => (c.id === id ? { ...c, status: targetComp.status } : c))
            );
            setAlert({ type: 'error', message: 'Failed to update component status on server.' });
            setTimeout(() => setAlert(null), 3000);
        }
    };

    // ── Open Add Form ─────────────────────────────────────────────────────────
    const handleOpenAdd = () => {
        setEditingComponent(null);
        setFormData(EMPTY_FORM);
        setFormErrors({});
        setShowForm(true);
    };

    // ── Open Edit Form (GET /api/components/{id}/) ────────────────────────────
    const handleOpenEdit = async (comp) => {
        setEditingComponent(comp);

        // Find initial category_id
        let catId = comp.category_id ? String(comp.category_id) : '';
        if (!catId && comp.category) {
            const match = categories.find(c => c.name.toLowerCase() === comp.category.toLowerCase());
            if (match) catId = String(match.id);
        }

        setFormData({
            code: comp.code || '',
            name: comp.name || '',
            category_id: catId,
            lookup_uom_id: comp.lookup_uom_id ? String(comp.lookup_uom_id) : '',
            unitOfMeasure: comp.unitOfMeasure || '',
            description: comp.description || '',
            specification: comp.specification || '',
            status: comp.status ?? true,
        });
        setFormErrors({});
        setShowForm(true);

        // Optionally fetch fresh single-record data
        try {
            const accessToken = localStorage.getItem('accessToken');
            if (accessToken && comp.id) {
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/components/${comp.id}/`, {
                    method: 'GET',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                });
                if (res.ok) {
                    const singleData = await res.json();
                    const record = singleData.data || singleData;
                    if (record) {
                        setFormData(prev => ({
                            ...prev,
                            code: record.component_code ?? prev.code,
                            name: record.component_name ?? prev.name,
                            category_id: record.category_id ? String(record.category_id) : prev.category_id,
                            lookup_uom_id: record.lookup_uom_id ? String(record.lookup_uom_id) : prev.lookup_uom_id,
                            unitOfMeasure: record.unit_of_measure ?? prev.unitOfMeasure,
                            description: record.component_description ?? prev.description,
                            specification: record.specification ?? prev.specification,
                            status: record.status !== undefined ? Boolean(record.status) : prev.status,
                        }));
                    }
                }
            }
        } catch (err) {
            console.error('[DesignComponents] Failed to fetch single component:', err);
        }
    };

    // ── Handle Delete (DELETE /api/components/{id}/delete/) ───────────────────
    const handleConfirmDelete = async () => {
        if (!pendingDelete) return;
        const accessToken = localStorage.getItem('accessToken');
        setIsSubmitting(true);

        try {
            const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/components/${pendingDelete}/delete/`, {
                method: 'DELETE',
                headers: {
                    'Authorization': `Bearer ${accessToken}`,
                    'Content-Type': 'application/json',
                },
            });

            if (!res.ok) {
                throw new Error(`Delete failed with status ${res.status}`);
            }

            setComponents(prev => prev.filter(c => c.id !== pendingDelete));
            setAlert({ type: 'success', message: 'Component deleted successfully.' });
            setTimeout(() => setAlert(null), 3000);
            setPendingDelete(null);
        } catch (error) {
            console.error('[DesignComponents] Delete error:', error);
            setAlert({ type: 'error', message: error.message || 'Failed to delete component.' });
            setTimeout(() => setAlert(null), 3000);
        } finally {
            setIsSubmitting(false);
        }
    };

    // ── Form Input Handler ────────────────────────────────────────────────────
    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
        if (formErrors[name]) {
            setFormErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    // ── Form Submit (POST /api/components/create/ OR PUT /api/components/{id}/update/) ──
    const handleFormSubmit = async (e) => {
        e.preventDefault();
        const errors = {};
        if (!formData.code.trim()) errors.code = 'Component Code is required.';
        if (!formData.name.trim()) errors.name = 'Component Name is required.';
        if (!formData.category_id) errors.category_id = 'Category is required.';
        if (!formData.unitOfMeasure.trim() && !formData.lookup_uom_id) errors.unitOfMeasure = 'Unit of Measure is required.';

        if (Object.keys(errors).length > 0) {
            setFormErrors(errors);
            return;
        }

        const accessToken = localStorage.getItem('accessToken');
        setIsSubmitting(true);

        const payload = {
            component_code: formData.code.trim(),
            component_name: formData.name.trim(),
            component_description: formData.description.trim(),
            category_id: parseInt(formData.category_id, 10),
            specification: formData.specification.trim(),
        };

        if (formData.lookup_uom_id) {
            payload.lookup_uom_id = parseInt(formData.lookup_uom_id, 10);
        }
        if (formData.unitOfMeasure) {
            payload.unit_of_measure = formData.unitOfMeasure.trim();
        }


        try {
            if (editingComponent) {
                // ── PUT /api/components/{id}/update/ ──
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/components/${editingComponent.id}/update/`, {
                    method: 'PUT',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(extractApiErrorMessage(errData, `Update failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Component "${formData.name}" updated successfully!` });
            } else {
                // ── POST /api/components/create/ ──
                const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/components/create/`, {
                    method: 'POST',
                    headers: {
                        'Authorization': `Bearer ${accessToken}`,
                        'Content-Type': 'application/json',
                    },
                    body: JSON.stringify(payload),
                });

                if (!res.ok) {
                    const errData = await res.json().catch(() => ({}));
                    throw new Error(extractApiErrorMessage(errData, `Creation failed (${res.status})`));
                }

                setAlert({ type: 'success', message: `Component "${formData.name}" added successfully!` });
            }

            setTimeout(() => setAlert(null), 3000);
            setShowForm(false);
            setEditingComponent(null);
            setFormData(EMPTY_FORM);
            await fetchComponents();
        } catch (error) {
            console.error('[DesignComponents] Form submit error:', error);
            setFormErrors(prev => ({ ...prev, submit: error.message || 'Operation failed. Please try again.' }));
            setAlert({ type: 'error', message: error.message || 'Error saving component.' });
            setTimeout(() => setAlert(null), 4000);
        } finally {
            setIsSubmitting(false);
        }
    };

    const isEditMode = Boolean(editingComponent);

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="assets-root"
        >
            {/* ══ 1. MAIN HEADER ══════════════════════════════════════════════════ */}
            <InnerHeader
                user={user}
                setView={setView}
                onProfilePhotoSuccess={async () => {
                    setTimeout(async () => { await fetchUserDetails(); }, 500);
                    setAlert({ type: 'success', message: 'Profile picture updated successfully!' });
                    setTimeout(() => setAlert(null), 3000);
                }}
            />

            {/* ══ 2. SECONDARY BAR ════════════════════════════════════════════════ */}
            <div className="assets-secondary-bar">
                <div className="assets-title-wrap">
                    <div className="assets-plus-icon">
                        <Plus size={13} />
                    </div>
                    <span className="assets-page-title">POLE DESIGN</span>
                </div>
                <div className="assets-top-nav">
                    {/* Navigation buttons commented out as requested - preserved for future use
                    {['Home', 'Dashboard', 'Analytics', 'Export Reports', 'Asset', 'Supplier', 'Pole Design', 'Maintenance']
                        .filter(label => {
                            if (label === 'Home') return true;
                            const role = user?.role;
                            const normRole = role ? role.replace(/[-_ ]/g, '').toLowerCase() : '';
                            if (label === 'Analytics' && normRole !== 'superadmin') return false;
                            if (normRole === 'technicalsupport') return true;
                            if (normRole === 'superadmin') return true;
                            if (normRole === 'government') return label === 'Dashboard';
                            if (normRole === 'maintenance' || normRole === 'research') {
                                return label === 'Dashboard' || label === 'Maintenance';
                            }
                            if (normRole === 'assetmanagement') {
                                return label !== 'Maintenance' && label !== 'Export Reports';
                            }
                            return true;
                        })
                        .map(label => (
                            <button
                                key={label}
                                className={`assets-top-nav-btn${activeTopNav === label ? ' active' : ''}`}
                                disabled={label === 'Export Reports'}
                                style={label === 'Export Reports' ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
                                onClick={() => {
                                    setActiveTopNav(label);
                                    if (label === 'Home') setView && setView('home');
                                    else if (label === 'Dashboard') setView && setView('dashboard');
                                    else if (label === 'Analytics') setView && setView('analytics');
                                    else if (label === 'Asset') setView && setView('assets-list');
                                    else if (label === 'Supplier') setView && setView('supplier-list');
                                    else if (label === 'Pole Design') setView && setView('pole-designs');
                                    else if (label === 'Maintenance') setView && setView('maintenance');
                                }}
                            >
                                {label}
                            </button>
                        ))}
                    */}
                    <button
                        className="assets-top-nav-btn"
                        onClick={() => setView && setView('home')}
                        title="Back to Home"
                    >
                        ← Home
                    </button>
                </div>
            </div>

            {/* ══ 3. BODY ═════════════════════════════════════════════════════════ */}
            <div className="assets-body">
                {/* Sidebar */}
                <PoleDesignSidebar setView={setView} activeItem="Design Components" />

                {/* Main Content Area */}
                <div className="assets-main">
                    {/* Alert Banner */}
                    {alert && (
                        <motion.div
                            initial={{ opacity: 0, y: -10 }}
                            animate={{ opacity: 1, y: 0 }}
                            className={`assets-alert ${alert.type}`}
                        >
                            {alert.type === 'error' ? <AlertCircle size={18} /> : <CheckCircle size={18} />}
                            <span className="alert-message-flex">{alert.message}</span>
                            <button className="assets-alert-close" onClick={() => setAlert(null)}>×</button>
                        </motion.div>
                    )}

                    {/* ── FORM VIEW (Add / Edit Component) ────────────────────── */}
                    {showForm ? (
                        <motion.div
                            key="component-form"
                            initial={{ opacity: 0, y: 8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0 }}
                            className="comp-form-card"
                        >
                            <div className="comp-form-header">
                                <h2 className="comp-form-title">
                                    {isEditMode ? 'Edit Component' : 'Add New Component'}
                                </h2>
                                <p className="comp-form-subtitle">
                                    {isEditMode
                                        ? 'Update the component details below.'
                                        : 'Enter component details to save into the design library.'}
                                </p>
                            </div>

                            {formErrors.submit && (
                                <div className="site-form-error" style={{ marginBottom: 14, fontSize: 13 }}>
                                    {formErrors.submit}
                                </div>
                            )}

                            <form onSubmit={handleFormSubmit} noValidate>
                                {/* Component Code */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Component Code *</label>
                                    <input
                                        className={`comp-form-input${formErrors.code ? ' error' : ''}`}
                                        type="text"
                                        name="code"
                                        placeholder="e.g. FAN-TEST-002"
                                        value={formData.code}
                                        onChange={handleFormChange}
                                        autoFocus
                                    />
                                    {formErrors.code && <p className="site-form-error">{formErrors.code}</p>}
                                </div>

                                {/* Component Name */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Component Name *</label>
                                    <input
                                        className={`comp-form-input${formErrors.name ? ' error' : ''}`}
                                        type="text"
                                        name="name"
                                        placeholder="e.g. Test Air Cleaning Fan two"
                                        value={formData.name}
                                        onChange={handleFormChange}
                                    />
                                    {formErrors.name && <p className="site-form-error">{formErrors.name}</p>}
                                </div>

                                {/* Category Dropdown */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Category *</label>
                                    <div className="comp-form-select-wrap">
                                        <select
                                            className={`comp-form-select${formErrors.category_id ? ' error' : ''}`}
                                            name="category_id"
                                            value={formData.category_id}
                                            onChange={handleFormChange}
                                        >
                                            <option value="">Select Category</option>
                                            {categories.map(cat => (
                                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                                            ))}
                                        </select>
                                        <ChevronDown size={14} className="comp-form-select-icon" />
                                    </div>
                                    {formErrors.category_id && <p className="site-form-error">{formErrors.category_id}</p>}
                                </div>

                                {/* Unit of Measure */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Unit of Measure *</label>
                                    {uoms.length > 0 ? (
                                        <div className="comp-form-select-wrap">
                                            <select
                                                className={`comp-form-select${formErrors.unitOfMeasure ? ' error' : ''}`}
                                                name="lookup_uom_id"
                                                value={formData.lookup_uom_id}
                                                onChange={(e) => {
                                                    const selectedId = e.target.value;
                                                    const found = uoms.find(u => String(u.id) === String(selectedId));
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        lookup_uom_id: selectedId,
                                                        unitOfMeasure: found ? (found.code || found.value) : ''
                                                    }));
                                                    if (formErrors.unitOfMeasure) {
                                                        setFormErrors(prev => ({ ...prev, unitOfMeasure: '' }));
                                                    }
                                                }}
                                            >
                                                <option value="">Select Unit of Measure</option>
                                                {uoms.map(u => (
                                                    <option key={u.id} value={u.id}>
                                                        {u.code} - {u.value}
                                                    </option>
                                                ))}
                                            </select>
                                            <ChevronDown size={14} className="comp-form-select-icon" />
                                        </div>
                                    ) : (
                                        <input
                                            className={`comp-form-input${formErrors.unitOfMeasure ? ' error' : ''}`}
                                            type="text"
                                            name="unitOfMeasure"
                                            placeholder="e.g. EA, EA2, Unit, Meter"
                                            value={formData.unitOfMeasure}
                                            onChange={handleFormChange}
                                        />
                                    )}
                                    {formErrors.unitOfMeasure && <p className="site-form-error">{formErrors.unitOfMeasure}</p>}
                                </div>


                                {/* Description */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Description</label>
                                    <textarea
                                        className="comp-form-textarea"
                                        name="description"
                                        placeholder="Enter component description..."
                                        value={formData.description}
                                        onChange={handleFormChange}
                                        rows={3}
                                    />
                                </div>

                                {/* Specification */}
                                <div className="comp-form-group">
                                    <label className="comp-form-label">Specification</label>
                                    <textarea
                                        className="comp-form-textarea"
                                        name="specification"
                                        placeholder="Enter technical specification (e.g. 220V AC2)..."
                                        value={formData.specification}
                                        onChange={handleFormChange}
                                        rows={3}
                                    />
                                </div>

                                {/* Form Actions */}
                                <div className="comp-form-actions">
                                    <button
                                        type="button"
                                        className="comp-btn-cancel"
                                        disabled={isSubmitting}
                                        onClick={() => {
                                            setShowForm(false);
                                            setEditingComponent(null);
                                            setFormData(EMPTY_FORM);
                                        }}
                                    >
                                        Cancel
                                    </button>
                                    <button type="submit" className="comp-btn-save" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                                <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
                                                Saving...
                                            </span>
                                        ) : (
                                            isEditMode ? 'Save Component' : 'Save Component'
                                        )}
                                    </button>
                                </div>
                            </form>
                        </motion.div>
                    ) : (
                        /* ── TABLE VIEW (Components List) ─────────────────────── */
                        <div className="comp-card">
                            <div className="comp-header-section" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                <div>
                                    <h1 className="comp-page-title">Components</h1>
                                    <p className="comp-page-subtitle">
                                        Manage reusable components for pole designs and supplier sourcing.
                                    </p>
                                </div>
                                <button
                                    onClick={fetchComponents}
                                    className="comp-page-btn"
                                    title="Refresh component list"
                                    style={{ display: 'flex', alignItems: 'center', gap: 4, height: 32, padding: '0 10px' }}
                                >
                                    <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} style={isLoading ? { animation: 'spin 1s linear infinite' } : {}} />
                                    <span>Refresh</span>
                                </button>
                            </div>

                            {/* Toolbar */}
                            <div className="comp-toolbar">
                                <div className="comp-search-wrap">
                                    <Search size={14} color="#64748b" />
                                    <input
                                        className="comp-search-input"
                                        placeholder="Search components..."
                                        value={searchQuery}
                                        onChange={e => {
                                            setSearchQuery(e.target.value);
                                            setCurrentPage(1);
                                        }}
                                    />
                                </div>
                                <button className="comp-add-btn" onClick={handleOpenAdd}>
                                    Add New Component
                                </button>
                            </div>

                            {/* Table */}
                            <div className="comp-table-wrap">
                                <table className="comp-table">
                                    <thead>
                                        <tr>
                                            <th
                                                className="comp-th sortable comp-th-expand"
                                                onClick={() => handleSort('id')}
                                            >
                                                ID
                                                <ChevronsUpDown size={12} style={{ display: 'inline', marginLeft: 4, verticalAlign: 'middle' }} />
                                            </th>
                                            <th
                                                className="comp-th sortable"
                                                onClick={() => handleSort('code')}
                                            >
                                                Component Code
                                                {sortField === 'code' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th
                                                className="comp-th sortable"
                                                onClick={() => handleSort('name')}
                                            >
                                                Component Name
                                                {sortField === 'name' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th
                                                className="comp-th sortable"
                                                onClick={() => handleSort('category')}
                                            >
                                                Category
                                                {sortField === 'category' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th
                                                className="comp-th sortable"
                                                onClick={() => handleSort('unitOfMeasure')}
                                            >
                                                Unit of Measure
                                                {sortField === 'unitOfMeasure' && (sortDir === 'asc' ? ' ↑' : ' ↓')}
                                            </th>
                                            <th className="comp-th comp-th-status">Status</th>
                                            <th className="comp-th comp-th-action">Action</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {isLoading ? (
                                            <tr>
                                                <td colSpan={7} className="comp-td" style={{ textAlign: 'center', padding: '40px 14px' }}>
                                                    <Loader2 size={24} style={{ display: 'inline-block', color: 'var(--primary)', animation: 'spin 1s linear infinite' }} />
                                                    <div style={{ marginTop: '10px', color: '#94a3b8', fontSize: '13px' }}>Loading components...</div>
                                                </td>
                                            </tr>
                                        ) : paginated.length === 0 ? (
                                            <tr>
                                                <td colSpan={7} className="assets-td assets-table-empty" style={{ textAlign: 'center', padding: '30px' }}>
                                                    {searchQuery ? 'No matching components found.' : 'No components found. Click "Add New Component" to create one.'}
                                                </td>
                                            </tr>
                                        ) : (
                                            paginated.map((item) => {
                                                const isExpanded = Boolean(expandedRows[item.id]);
                                                const catName = item.category || categories.find(c => String(c.id) === String(item.category_id))?.name || '—';

                                                return (
                                                    <React.Fragment key={item.id}>
                                                        <tr className="comp-table-row">
                                                            {/* ID + Expand Toggle */}
                                                            <td className="comp-td">
                                                                <button
                                                                    className={`comp-expand-btn${isExpanded ? ' open' : ''}`}
                                                                    onClick={() => toggleRowExpand(item.id)}
                                                                    title="Expand Details"
                                                                >
                                                                    <ChevronRight size={14} />
                                                                </button>
                                                                <span>{item.id}</span>
                                                            </td>

                                                            {/* Component Code */}
                                                            <td className="comp-td">
                                                                <span className="comp-code-pill">{item.code}</span>
                                                            </td>

                                                            {/* Component Name */}
                                                            <td className="comp-td" style={{ fontWeight: 500, color: '#f1f5f9' }}>
                                                                {item.name}
                                                            </td>

                                                            {/* Category */}
                                                            <td className="comp-td">
                                                                <span className="comp-badge-cat">{catName}</span>
                                                            </td>

                                                            {/* Unit of Measure */}
                                                            <td className="comp-td">{item.unitOfMeasure}</td>

                                                            {/* Status Switch */}
                                                            <td className="comp-td comp-td-status">
                                                                <button
                                                                    className={`comp-toggle-btn ${item.status ? 'on' : ''}`}
                                                                    onClick={e => handleToggleStatus(item.id, e)}
                                                                    title={item.status ? 'Active' : 'Inactive'}
                                                                >
                                                                    <span className="comp-toggle-thumb" />
                                                                </button>
                                                            </td>

                                                            {/* Actions */}
                                                            <td className="comp-td comp-td-action">
                                                                <span className="assets-action-group">
                                                                    <button
                                                                        className="assets-action-btn edit"
                                                                        title="Edit"
                                                                        onClick={() => handleOpenEdit(item)}
                                                                    >
                                                                        <Edit2 size={14} />
                                                                    </button>
                                                                    <button
                                                                        className="assets-action-btn delete"
                                                                        title="Delete"
                                                                        onClick={() => setPendingDelete(item.id)}
                                                                    >
                                                                        <Trash2 size={14} />
                                                                    </button>
                                                                </span>
                                                            </td>
                                                        </tr>

                                                        {/* Expanded Details Row */}
                                                        {isExpanded && (
                                                            <tr className="comp-detail-row">
                                                                <td colSpan={7}>
                                                                    <div className="comp-detail-card" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '14px' }}>
                                                                        <div className="comp-detail-item">
                                                                            <span className="comp-detail-label">Description</span>
                                                                            <span className="comp-detail-value">
                                                                                {item.description || 'No description provided.'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="comp-detail-item">
                                                                            <span className="comp-detail-label">Technical Specification</span>
                                                                            <span className="comp-detail-value">
                                                                                {item.specification || 'No technical specification provided.'}
                                                                            </span>
                                                                        </div>
                                                                        <div className="comp-detail-item" style={{ gridColumn: 'span 2', paddingTop: '10px', borderTop: '1px solid rgba(8, 120, 255, 0.15)' }}>
                                                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px' }}>
                                                                                <div>
                                                                                    <span className="comp-detail-label">Created At (UTC)</span>
                                                                                    <span className="comp-detail-value" style={{ fontSize: 12, color: '#94a3b8' }}>
                                                                                        {item.created_utc ? new Date(item.created_utc).toLocaleString() : '—'}
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="comp-detail-label">Created By</span>
                                                                                    <span className="comp-detail-value" style={{ fontSize: 12, color: '#94a3b8' }}>
                                                                                        {item.created_by || '—'}
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="comp-detail-label">Last Updated Date</span>
                                                                                    <span className="comp-detail-value" style={{ fontSize: 12, color: '#94a3b8' }}>
                                                                                        {item.last_update_date ? new Date(item.last_update_date).toLocaleString() : '—'}
                                                                                    </span>
                                                                                </div>
                                                                                <div>
                                                                                    <span className="comp-detail-label">Last Updated By</span>
                                                                                    <span className="comp-detail-value" style={{ fontSize: 12, color: '#94a3b8' }}>
                                                                                        {item.last_updated_by || '—'}
                                                                                    </span>
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        )}
                                                    </React.Fragment>
                                                );
                                            })
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {/* Footer / Pagination */}
                            <div className="comp-footer">
                                <div className="comp-showing-text">
                                    Showing {sorted.length > 0 ? (currentPage - 1) * ROWS_PER_PAGE + 1 : 0} to{' '}
                                    {Math.min(currentPage * ROWS_PER_PAGE, sorted.length)} of {sorted.length} components
                                </div>

                                <div className="comp-pagination">
                                    <button
                                        className="comp-page-btn"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                    >
                                        &lt;
                                    </button>
                                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                                        <button
                                            key={page}
                                            className={`comp-page-btn${currentPage === page ? ' active' : ''}`}
                                            onClick={() => setCurrentPage(page)}
                                        >
                                            {page}
                                        </button>
                                    ))}
                                    <button
                                        className="comp-page-btn"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                    >
                                        &gt;
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Confirm Delete Modal ──────────────────────────────────────── */}
            <AnimatePresence>
                {pendingDelete && (
                    <ConfirmDeleteModal
                        message="Are you sure you want to delete this component? This action cannot be undone."
                        onConfirm={handleConfirmDelete}
                        onCancel={() => setPendingDelete(null)}
                    />
                )}
            </AnimatePresence>

            {/* ══ 4. FOOTER ═══════════════════════════════════════════════════════ */}
            <InnerFooter />
        </motion.div>
    );
};

export default DesignComponents;
